// ═══════════════════════════════════════════════════════════════
// MainStreet — Public Business Registration (no auth required)
// Business owners register their org + contact info. Creates an
// org record with status 'pending'. Admin approves + creates login.
// ═══════════════════════════════════════════════════════════════

import { useState, useRef } from 'react';
import {
  Box, Paper, Title, Text, TextInput, Select, Textarea, Button, Autocomplete,
  Group, Stack, SimpleGrid, Divider, ThemeIcon, Stepper,
} from '../../components/gab/GabComponents';
import { IconBuildingCommunity, IconCheck, IconUser, IconShieldCheck } from '@tabler/icons-react';
import { createRecord, updateRecord, TABLES, FIELDS, WORKER_URL } from '../../lib/api-switch';

const WORKER = WORKER_URL;

const SECTOR_OPTIONS = [
  { value: 'restaurant', label: 'Restaurant / Food Service' },
  { value: 'retail', label: 'Retail' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'construction', label: 'Construction' },
  { value: 'hospitality', label: 'Hospitality / Lodging' },
  { value: 'education', label: 'Education' },
  { value: 'nonprofit', label: 'Non-Profit' },
  { value: 'government', label: 'Government' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'technology', label: 'Technology' },
  { value: 'financial', label: 'Financial Services' },
  { value: 'transportation', label: 'Transportation / Logistics' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'other', label: 'Other' },
];

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function PublicBusinessSignup() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [regCode, setRegCode] = useState('');

  // Business info
  const [bizName, setBizName] = useState('');
  const [sector, setSector] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [bizPhone, setBizPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [employees, setEmployees] = useState('');
  const [description, setDescription] = useState('');

  // Contact info
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactTitle, setContactTitle] = useState('');

  // Address autocomplete
  const [addrSuggestions, setAddrSuggestions] = useState<string[]>([]);
  const addrDetailsRef = useRef<Record<string, { street: string; city: string; state: string; zip: string }>>({});
  const addrTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAddrSearch = (q: string) => {
    setAddress(q);
    // If user typed/selected a value that matches a known suggestion, fill fields immediately
    const known = addrDetailsRef.current[q];
    if (known) {
      if (known.street) setAddress(known.street);
      if (known.city) setCity(known.city);
      if (known.state) setState(known.state);
      if (known.zip) setZip(known.zip);
      setAddrSuggestions([]);
      return;
    }
    if (addrTimeout.current) clearTimeout(addrTimeout.current);
    if (q.length < 3) { setAddrSuggestions([]); return; }
    addrTimeout.current = setTimeout(async () => {
      try {
        const r = await fetch(`${WORKER}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`, {
          headers: { 'Origin': 'https://apps.opengov.com' },
        });
        if (!r.ok) return;
        const data = await r.json();
        const items = (data.items || []).filter((i: any) => i.address);
        const details: Record<string, { street: string; city: string; state: string; zip: string }> = {};
        const labels = items.map((i: any) => {
          const label = i.address.label || i.title || q;
          details[label] = {
            street: [i.address.houseNumber, i.address.street].filter(Boolean).join(' ') || label.split(',')[0] || '',
            city: i.address.city || '',
            state: i.address.stateCode || i.address.state || '',
            zip: i.address.postalCode || '',
          };
          return label;
        });
        addrDetailsRef.current = details;
        setAddrSuggestions(labels);
      } catch { /* ignore */ }
    }, 300);
  };
  const handleAddrSelect = (label: string) => {
    const d = addrDetailsRef.current[label];
    if (d) {
      if (d.street) setAddress(d.street);
      if (d.city) setCity(d.city);
      if (d.state) setState(d.state);
      if (d.zip) setZip(d.zip);
      setAddrSuggestions([]);
    }
  };

  const canAdvance = step === 0
    ? bizName.trim() && sector
    : contactName.trim() && contactEmail.trim();

  const handleSubmit = async () => {
    if (!bizName.trim() || !contactEmail.trim()) return;
    setSubmitting(true);
    try {
      const F = FIELDS.msOrganizations;
      const code = `BIZ-${Date.now().toString(36).toUpperCase()}`;

      // Create org record using real V2 field keys
      const fields: Array<[string, string]> = [
        [F.orgName, bizName.trim()],
        [F.sector, sector],
        [F.address, address.trim()],
        [F.city, city.trim()],
        [F.state, state.trim()],
        [F.zip, zip.trim()],
        [F.contactPhone, bizPhone],
        [F.contactEmail, contactEmail.trim()],
        [F.contactName, contactName.trim()],
        [F.opStatus, 'pending'],
      ];
      const rid = await createRecord(TABLES.msOrganizations, fields, { anonymous: true });

      // PUT remaining fields
      if (rid) {
        const extra: Array<[string, string]> = [
          [F.notes, JSON.stringify({
            registrationCode: code,
            contactTitle,
            contactPhone,
            website,
            employees,
            description,
            registeredAt: new Date().toISOString(),
            source: 'public_signup',
          })],
        ];
        await updateRecord(TABLES.msOrganizations, rid, extra, { anonymous: true });
      }

      setRegCode(code);
      setSubmitted(true);
    } catch (err) {
      console.error('Business registration failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success Screen ──────────────────────────────────────────

  if (submitted) {
    return (
      <Box mih="100vh" style={{
        background: 'linear-gradient(135deg, #0891b2 0%, #065f6b 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <Paper shadow="lg" radius="md" p="xl" maw={500} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={64} radius="xl" variant="light" color="var(--color-success)">
              <IconCheck size={36} />
            </ThemeIcon>
            <Title order={3} ta="center" c="#07963F">Registration Submitted</Title>
            <Text ta="center" c="dimmed">
              <strong>{bizName}</strong> has been registered. An administrator will review
              your submission and create your portal login.
            </Text>
            <Paper p="md" bg="gray.0" radius="md" withBorder w="100%">
              <Text size="xs" c="dimmed" ta="center">Your Registration Code</Text>
              <Text fw={700} size="xl" ta="center" ff="monospace" c="#0891b2" mt={4}>
                {regCode}
              </Text>
              <Text size="xs" c="dimmed" ta="center" mt={4}>
                Save this code — you'll need it when your account is activated.
              </Text>
            </Paper>
            <Paper p="sm" bg="blue.0" radius="md" withBorder w="100%">
              <Text size="sm" ta="center" c="blue.8">
                <strong>What happens next?</strong><br />
                1. An EMC administrator reviews your registration<br />
                2. They create your portal login credentials<br />
                3. You'll receive an email with login instructions<br />
                4. Log in to access damage reporting, status updates, and re-entry passes
              </Text>
            </Paper>
            <Button variant="light" onClick={() => {
              setSubmitted(false);
              setStep(0);
              setBizName(''); setSector(''); setAddress(''); setCity('');
              setState(''); setZip(''); setBizPhone('');
              setContactName(''); setContactEmail('');
            }}>
              Register Another Business
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // ── Registration Form ───────────────────────────────────────

  return (
    <Box mih="100vh" style={{
      background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)',
      padding: 24,
    }}>
      <Paper shadow="md" radius="md" p="xl" maw={700} mx="auto">
        <Stack align="center" gap="xs" mb="lg">
          <ThemeIcon size={48} radius="xl" variant="light" color="#03AC9E">
            <IconBuildingCommunity size={24} />
          </ThemeIcon>
          <Title order={3} c="#0891b2">Business Registration</Title>
          <Text size="sm" c="dimmed" ta="center" maw={500}>
            Register your business with the Emergency Management Center. Once approved,
            you'll get portal access for damage reporting, status updates, and re-entry passes.
          </Text>
        </Stack>

        <Stepper active={step} onStepClick={setStep} size="sm" mb="lg"
          color="#0891b2" allowNextStepsSelect={false}>
          <Stepper.Step label="Business Info" icon={<IconBuildingCommunity size={16} />} />
          <Stepper.Step label="Contact Info" icon={<IconUser size={16} />} />
          <Stepper.Step label="Review" icon={<IconShieldCheck size={16} />} />
        </Stepper>

        {/* Step 0: Business Info */}
        {step === 0 && (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="md">
              <TextInput label="Business Name *" value={bizName}
                onChange={e => setBizName(e.target.value)} required />
              <Select label="Business Sector *" data={SECTOR_OPTIONS} value={sector}
                onChange={v => setSector(v ?? '')} searchable placeholder="Select sector..." />
            </SimpleGrid>
            <Autocomplete label="Business Address" value={address}
              onChange={handleAddrSearch} onOptionSubmit={handleAddrSelect}
              data={addrSuggestions} placeholder="Start typing an address..." mb="sm" />
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm" mb="md">
              <TextInput label="City" value={city} onChange={e => setCity(e.target.value)} />
              <TextInput label="State" value={state} onChange={e => setState(e.target.value)} />
              <TextInput label="ZIP" value={zip} onChange={e => setZip(e.target.value)} />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="md">
              <TextInput label="Business Phone" value={bizPhone} placeholder="(555) 123-4567"
                onChange={e => setBizPhone(formatPhone(e.target.value))} />
              <TextInput label="Website" value={website} placeholder="https://"
                onChange={e => setWebsite(e.target.value)} />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="md">
              <TextInput label="Number of Employees" value={employees}
                onChange={e => setEmployees(e.target.value)} placeholder="Approximate" />
            </SimpleGrid>
            <Textarea label="Business Description" value={description}
              onChange={e => setDescription(e.target.value)} autosize minRows={2} mb="md" />
          </>
        )}

        {/* Step 1: Contact Info */}
        {step === 1 && (
          <>
            <Text size="sm" c="dimmed" mb="md">
              This person will be the primary contact and portal administrator for your business.
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="md">
              <TextInput label="Full Name *" value={contactName}
                onChange={e => setContactName(e.target.value)} required />
              <TextInput label="Email Address *" value={contactEmail}
                onChange={e => setContactEmail(e.target.value)} required
                placeholder="This will be your portal login" />
              <TextInput label="Phone" value={contactPhone} placeholder="(555) 123-4567"
                onChange={e => setContactPhone(formatPhone(e.target.value))} />
              <TextInput label="Title / Position" value={contactTitle}
                onChange={e => setContactTitle(e.target.value)} placeholder="e.g. Owner, Manager" />
            </SimpleGrid>
          </>
        )}

        {/* Step 2: Review */}
        {step === 2 && (
          <>
            <Paper p="md" bg="gray.0" radius="md" withBorder mb="md">
              <Text fw={600} size="sm" mb="sm">Business Information</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={4}>
                <Text size="xs" c="dimmed">Name</Text><Text size="sm" fw={500}>{bizName}</Text>
                <Text size="xs" c="dimmed">Sector</Text><Text size="sm">{SECTOR_OPTIONS.find(s => s.value === sector)?.label || sector}</Text>
                <Text size="xs" c="dimmed">Address</Text><Text size="sm">{[address, city, state, zip].filter(Boolean).join(', ') || '-'}</Text>
                <Text size="xs" c="dimmed">Phone</Text><Text size="sm">{bizPhone || '-'}</Text>
              </SimpleGrid>
            </Paper>
            <Paper p="md" bg="gray.0" radius="md" withBorder mb="md">
              <Text fw={600} size="sm" mb="sm">Primary Contact</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={4}>
                <Text size="xs" c="dimmed">Name</Text><Text size="sm" fw={500}>{contactName}</Text>
                <Text size="xs" c="dimmed">Email</Text><Text size="sm">{contactEmail}</Text>
                <Text size="xs" c="dimmed">Phone</Text><Text size="sm">{contactPhone || '-'}</Text>
                <Text size="xs" c="dimmed">Title</Text><Text size="sm">{contactTitle || '-'}</Text>
              </SimpleGrid>
            </Paper>
          </>
        )}

        <Divider my="md" />

        {/* Navigation */}
        <Group justify="space-between">
          {step > 0 ? (
            <Button variant="default" onClick={() => setStep(s => s - 1)}>Back</Button>
          ) : <div />}
          {step < 2 ? (
            <Button onClick={() => setStep(s => s + 1)} disabled={!canAdvance}
              color="#0891b2">
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} loading={submitting}
              disabled={!bizName.trim() || !contactEmail.trim()}
              color="#0891b2" size="lg">
              Submit Registration
            </Button>
          )}
        </Group>

        <Text size="xs" c="dimmed" ta="center" mt="lg">
          OpenGov Emergency Management Center
        </Text>
      </Paper>
    </Box>
  );
}
